/**
 * Email Queue — every email submitted for extraction, and where each one is
 * on its way from the queue to processed.
 *
 * The status tiles at the top are the overview and the filter in one: each
 * shows its count and share of the total for the current search and time
 * range, and selecting one narrows the list to it. The list itself reads like
 * an inbox, grouped under the same date labels the dashboard uses.
 *
 * While anything is queued or processing, the page refreshes itself quietly
 * (no skeleton, no reshuffle). New arrivals flash in and a status that moved
 * on gets a small pop, so a change is seen without hunting for it.
 *
 * Filters live in the URL (page, status, search) exactly as before, so links
 * from the dashboard and bookmarks keep working. Search applies on Enter.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { emailsApi } from '../services/api'
import { formatDate, formatDateTime, formatTimeAgo, getDateGroupLabel } from '../utils/dateUtils'
import IngestEmailModal from '../components/IngestEmailModal'
import { CountUp } from '../components/billing/BillingPrimitives'
import { trackSpotlight } from '../components/collection/cardInteractions'
import useSegmentedTabIndicator from '../hooks/useSegmentedTabIndicator'
import EmailStatusBadge from '../components/emails/EmailStatusBadge'
import { LIVE_INTERVAL_MS, STATUS_ORDER, avatarTone, getStatusMeta, initialsFor, parseSender } from '../components/emails/emailStatus'
import { useAuth } from '../context/AuthContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Mail,
  MailPlus,
  Inbox,
  RefreshCw,
  AlertCircle,
  Paperclip,
  Eye,
  SearchX,
  X,
  ArrowUpRight,
  Pause,
} from 'lucide-react'

// The API's 'week' and 'month' are rolling 7 and 30 days, so they're named that way.
const TIME_RANGES = [
  { value: '', label: 'All time', short: 'All time' },
  { value: 'today', label: 'Today', short: 'Today' },
  { value: 'week', label: 'Last 7 days', short: '7 days' },
  { value: 'month', label: 'Last 30 days', short: '30 days' },
]

const PAGE_SIZES = [10, 25, 50]

// How long an arrival or status change stays highlighted.
const HIGHLIGHT_MS = 2600

function Emails() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()
  const isReader = isReadOnlyUser(user)
  const [emails, setEmails] = useState([])
  const [stats, setStats] = useState({})
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [perPage, setPerPage] = useState(10)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [showIngestModal, setShowIngestModal] = useState(false)
  const [timeRange, setTimeRange] = useState('')
  const [livePaused, setLivePaused] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [highlights, setHighlights] = useState({})
  const [listVersion, setListVersion] = useState(0)

  const page = parseInt(searchParams.get('page') || '1')
  const status = searchParams.get('status') || ''
  const search = searchParams.get('search') || ''

  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => { setSearchInput(search) }, [search])

  const requestSeq = useRef(0)
  const seenRef = useRef(new Map())
  const idsSigRef = useRef('')
  const highlightTimer = useRef(null)
  const listTopRef = useRef(null)

  /**
   * `silent` is the live refresh: no loading state, and differences from the
   * last result are highlighted rather than the list replaying its entrance.
   */
  const fetchEmails = useCallback(async ({ silent = false } = {}) => {
    const seq = ++requestSeq.current
    try {
      if (!silent) setLoading(true)
      const [res, statsRes] = await Promise.all([
        emailsApi.list({
          page,
          per_page: perPage,
          status: status || undefined,
          search: search || undefined,
          time_range: timeRange || undefined,
        }),
        emailsApi.stats({
          search: search || undefined,
          time_range: timeRange || undefined,
        }),
      ])
      // A slower, older response must not overwrite a newer one.
      if (seq !== requestSeq.current) return
      const next = res.data.emails || []

      if (silent) {
        const changes = {}
        next.forEach(email => {
          if (!seenRef.current.has(email.id)) changes[email.id] = 'new'
          else if (seenRef.current.get(email.id) !== email.status) changes[email.id] = 'status'
        })
        if (Object.keys(changes).length) {
          setHighlights(changes)
          clearTimeout(highlightTimer.current)
          highlightTimer.current = setTimeout(() => setHighlights({}), HIGHLIGHT_MS)
        }
      } else {
        const sig = next.map(e => e.id).join(',')
        if (sig !== idsSigRef.current) setListVersion(v => v + 1)
        idsSigRef.current = sig
        setHighlights({})
      }
      seenRef.current = new Map(next.map(e => [e.id, e.status]))

      setEmails(next)
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 })
      setStats(statsRes.data || {})
      setLastUpdated(new Date())
      setError(null)
    } catch (err) {
      if (seq !== requestSeq.current) return
      console.error('Error fetching emails:', err)
      // A failed background refresh keeps what's on screen; only a requested
      // load reports the failure.
      if (!silent) setError('Emails could not be loaded.')
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false)
        setHasLoaded(true)
      }
    }
  }, [page, status, search, timeRange, perPage])

  useEffect(() => {
    fetchEmails()
  }, [fetchEmails])

  useEffect(() => () => clearTimeout(highlightTimer.current), [])

  const inFlight = (stats.pending || 0) + (stats.processing || 0)
  const liveActive = hasLoaded && inFlight > 0 && !livePaused && !error

  useEffect(() => {
    if (!liveActive) return undefined
    const id = setInterval(() => {
      if (document.visibilityState === 'visible' && !showIngestModal) fetchEmails({ silent: true })
    }, LIVE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [liveActive, fetchEmails, showIngestModal])

  // Empty values are dropped rather than written as `?status=`.
  const updateParams = (patch) => {
    setSearchParams(prev => {
      const params = { ...Object.fromEntries(prev), ...patch }
      Object.keys(params).forEach(key => { if (params[key] === '' || params[key] == null) delete params[key] })
      return params
    })
  }

  const handleSearch = (e) => {
    e.preventDefault()
    updateParams({ search: searchInput.trim(), page: '1' })
  }

  const handleSearchChange = (value) => {
    setSearchInput(value)
    // Clearing the box clears the search straight away, as before.
    if (value === '' && search) updateParams({ search: '', page: '1' })
  }

  const handleClearSearch = () => {
    setSearchInput('')
    updateParams({ search: '', page: '1' })
  }

  const handleStatusFilter = (newStatus) => {
    updateParams({ status: newStatus, page: '1' })
  }

  const handleTimeRange = (value) => {
    setTimeRange(value)
    updateParams({ page: '1' })
  }

  const handleClearAll = () => {
    setSearchInput('')
    setTimeRange('')
    updateParams({ search: '', status: '', page: '1' })
  }

  // A new page starts at its top, not wherever the last one was scrolled to.
  const handlePageChange = (newPage) => {
    updateParams({ page: newPage.toString() })
    const top = listTopRef.current?.getBoundingClientRect().top
    if (top != null && top < 64) {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: window.scrollY + top - 80, behavior: reducedMotion ? 'auto' : 'smooth' })
    }
  }

  const handleIngestSuccess = () => {
    setShowIngestModal(false)
    fetchEmails()
  }

  // Consecutive emails sharing a date label form one group.
  const groups = useMemo(() => {
    const result = []
    emails.forEach((email, idx) => {
      const label = getDateGroupLabel(email.received_at || email.created_at)
      const last = result[result.length - 1]
      if (last && last.label === label) last.entries.push({ email, idx })
      else result.push({ label, entries: [{ email, idx }] })
    })
    return result
  }, [emails])

  const totalPages = Math.ceil(pagination.total / perPage)
  const refetching = loading && hasLoaded
  const rangeStart = pagination.total === 0 ? 0 : (page - 1) * perPage + 1
  const rangeEnd = Math.min(page * perPage, pagination.total)
  const timeLabel = TIME_RANGES.find(r => r.value === timeRange)?.label
  const activeFilters = [status, search, timeRange].filter(Boolean).length

  const counts = {
    '': stats.total ?? pagination.total,
    pending: stats.pending || 0,
    processing: stats.processing || 0,
    processed: stats.processed || 0,
    failed: stats.failed || 0,
  }

  return (
    <div className="space-y-6 animate-view-in">
      <PageHeader
        total={hasLoaded ? counts[''] : null}
        isReader={isReader}
        onIngest={() => { if (!isReader) setShowIngestModal(true) }}
      />

      <StatusOverview
        counts={counts}
        loaded={hasLoaded}
        active={status}
        onSelect={handleStatusFilter}
      />

      <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900" style={{ '--rise-delay': '120ms' }}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3.5 dark:border-gray-800 sm:px-5">
          <SearchBox
            value={searchInput}
            applied={search}
            onChange={handleSearchChange}
            onSubmit={handleSearch}
            onClear={handleClearSearch}
            busy={loading && Boolean(search)}
          />
          <TimeRangeControl value={timeRange} onChange={handleTimeRange} />
          <div className="ml-auto flex items-center gap-2">
            <LiveIndicator
              inFlight={inFlight}
              active={liveActive}
              paused={livePaused}
              lastUpdated={lastUpdated}
              onToggle={() => setLivePaused(p => !p)}
            />
            <button
              type="button"
              onClick={() => fetchEmails()}
              disabled={loading}
              aria-label="Refresh emails"
              title="Refresh"
              className="inline-flex h-9 flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-[13px] font-medium text-gray-600 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100 sm:px-3"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary line */}
        <div ref={listTopRef} className="flex min-h-[2.75rem] flex-wrap items-center gap-x-2 gap-y-1.5 px-4 pt-3 text-sm text-gray-500 dark:text-gray-400 sm:px-5">
          {!hasLoaded ? (
            <div className="skeleton h-4 w-44" />
          ) : pagination.total > 0 ? (
            <span>
              Showing <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{rangeStart}&ndash;{rangeEnd}</span> of{' '}
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{pagination.total}</span> email{pagination.total !== 1 ? 's' : ''}
            </span>
          ) : (
            <span>No emails</span>
          )}
          {status && (
            <FilterPill
              label={getStatusMeta(status).label}
              dot={getStatusMeta(status).bar}
              onRemove={() => handleStatusFilter('')}
              removeLabel={`Remove ${getStatusMeta(status).label} filter`}
            />
          )}
          {search && (
            <FilterPill label={<>Matching &ldquo;{search}&rdquo;</>} onRemove={handleClearSearch} removeLabel="Clear search" />
          )}
          {timeRange && (
            <FilterPill label={timeLabel} onRemove={() => handleTimeRange('')} removeLabel={`Remove ${timeLabel} filter`} />
          )}
          {activeFilters > 1 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs font-medium text-gray-500 underline-offset-2 transition-colors hover:text-gray-800 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          )}
        </div>

        {error && (
          <div role="alert" className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 animate-panel-in dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 sm:mx-5">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={() => fetchEmails()} className="font-semibold hover:underline">Retry</button>
          </div>
        )}

        {/* Refetch over existing results: a sliver of progress, and the list
            dims instead of blanking. */}
        <div className="relative mt-3 h-0.5 overflow-hidden" aria-hidden="true">
          {refetching && (
            <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
          )}
        </div>

        <div aria-busy={loading} className={`transition-opacity duration-200 ${refetching ? 'opacity-60' : 'opacity-100'}`}>
          {!hasLoaded ? (
            <ListSkeleton />
          ) : emails.length === 0 ? (
            !error && (
              <EmptyState
                status={status}
                search={search}
                timeLabel={timeRange ? timeLabel : null}
                isReader={isReader}
                onClear={handleClearAll}
                onIngest={() => setShowIngestModal(true)}
              />
            )
          ) : (
            <div key={listVersion}>
              <ColumnHeader />
              {groups.map((group, groupIdx) => (
                <section key={`${group.label}-${groupIdx}`} aria-label={group.label}>
                  <DateHeader label={group.label} count={group.entries.length} />
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800/80">
                    {group.entries.map(({ email, idx }) => (
                      <EmailRow key={email.id} email={email} index={idx} highlight={highlights[email.id]} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          perPage={perPage}
          onPage={handlePageChange}
          onPerPage={(size) => {
            setPerPage(size)
            handlePageChange(1)
          }}
        />
      </div>

      {/* Ingest Modal */}
      {showIngestModal && (
        <IngestEmailModal
          onClose={() => setShowIngestModal(false)}
          onSuccess={handleIngestSuccess}
        />
      )}
    </div>
  )
}

// ── Header ───────────────────────────────────────────────────────────────────

function PageHeader({ total, isReader, onIngest }) {
  return (
    <div className="flex flex-col gap-4 animate-header-in sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3.5">
        <div className="group hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10 sm:flex">
          <Mail className="h-5 w-5 transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:-rotate-12 group-hover:scale-110" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Email Queue</h1>
            {total != null && (
              <span key={total} className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-navy-700 ring-1 ring-inset ring-navy-200 animate-panel-in dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitor incoming emails and their attachments as they move from the queue to processed.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onIngest}
        disabled={isReader}
        title={isReader ? READ_ONLY_MESSAGE : undefined}
        className={`btn group flex-shrink-0 self-start sm:self-auto ${isReader ? 'btn-secondary cursor-not-allowed opacity-50' : 'btn-primary shadow-sm'}`}
      >
        <Plus className={`mr-2 h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${isReader ? '' : 'group-hover:rotate-90'}`} />
        Ingest Email
      </button>
    </div>
  )
}

// ── Status overview ──────────────────────────────────────────────────────────

function sharePct(count, total) {
  if (!total) return 0
  return (count / total) * 100
}

function formatShare(count, total) {
  if (!total || !count) return '0%'
  const pct = sharePct(count, total)
  return pct < 1 ? '<1%' : `${Math.round(pct)}%`
}

/**
 * One card per status. Each is a toggle for the list's status filter, so the
 * overview and the filter can't disagree about what's selected.
 */
function StatusOverview({ counts, loaded, active, onSelect }) {
  const total = counts[''] || 0
  const finished = counts.processed + counts.failed
  const successRate = finished ? Math.round((counts.processed / finished) * 100) : null

  const foot = (key) => {
    if (!loaded) return null
    if (key === '') return successRate == null ? 'Nothing finished yet' : `${successRate}% processed successfully`
    if (key === 'processing' && counts.processing > 0) return 'Working on it now'
    if (key === 'failed' && counts.failed > 0) return `Needs attention · ${formatShare(counts.failed, total)}`
    return `${formatShare(counts[key], total)} of emails`
  }

  return (
    <div role="group" aria-label="Filter by status" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {STATUS_ORDER.map((key, i) => {
        const meta = getStatusMeta(key)
        const Icon = meta.icon
        const selected = active === key
        const count = counts[key] || 0
        const live = key === 'processing' && count > 0
        return (
          <button
            key={key || 'all'}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(key)}
            onMouseMove={trackSpotlight}
            style={{ '--card-glow': meta.glow, '--rise-delay': `${i * 45}ms` }}
            className={`collection-card group relative rounded-2xl border bg-white p-4 text-left animate-rise-in transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 active:translate-y-0 dark:bg-gray-900 ${
              i === 0 ? 'col-span-2 sm:col-span-1' : ''
            } ${
              selected
                ? `border-transparent bg-gradient-to-br to-transparent shadow-sm ring-2 ${meta.ring} ${meta.tint}`
                : 'border-gray-200 dark:border-gray-800'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={`text-[13px] font-medium ${selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                {meta.label}
              </span>
              <span className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:-rotate-6 group-hover:scale-110 ${meta.chip}`}>
                <Icon className={`h-4 w-4 ${meta.spin && live ? 'motion-safe:animate-spin' : ''}`} />
                {live && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75 motion-safe:animate-ping" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-sky-500 ring-2 ring-white dark:ring-gray-900" />
                  </span>
                )}
              </span>
            </div>

            <p className="mt-2 text-[28px] font-bold leading-none tracking-tight text-gray-900 dark:text-gray-100">
              {loaded ? <CountUp value={count} /> : <span className="skeleton inline-block h-7 w-12 align-middle" />}
            </p>

            {/* All: the whole pipeline as one stacked bar. Others: their share. */}
            <div className="mt-3 flex h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800" aria-hidden="true">
              {loaded && (key === ''
                ? STATUS_ORDER.slice(1).map((part, j) => (
                  <span
                    key={part}
                    className={`h-full animate-grow-x transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${getStatusMeta(part).bar}`}
                    style={{ width: `${sharePct(counts[part], total)}%`, animationDelay: `${200 + j * 90}ms` }}
                  />
                ))
                : (
                  <span
                    className={`h-full rounded-full animate-grow-x transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${meta.bar}`}
                    style={{ width: `${sharePct(count, total)}%`, animationDelay: `${200 + i * 60}ms` }}
                  />
                ))}
            </div>

            <p className={`mt-2 truncate text-[11px] ${key === 'failed' && count > 0 ? 'font-medium text-rose-600 dark:text-rose-300' : 'text-gray-400 dark:text-gray-500'}`}>
              {foot(key) ?? <span className="skeleton inline-block h-3 w-20 align-middle" />}
            </p>
          </button>
        )
      })}
    </div>
  )
}

// ── Toolbar ──────────────────────────────────────────────────────────────────

function SearchBox({ value, applied, onChange, onSubmit, onClear, busy }) {
  const inputRef = useRef(null)
  const [focused, setFocused] = useState(false)
  const pending = value.trim() !== '' && value.trim() !== applied

  // "/" jumps to search from anywhere on the page, as in the other list views.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <form onSubmit={onSubmit} role="search" className="relative w-full min-w-0 sm:w-auto sm:flex-1 lg:max-w-md">
      <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${focused ? 'text-navy-500 dark:text-navy-300' : 'text-gray-400'}`} />
      <input
        ref={inputRef}
        type="search"
        name="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value || applied) onClear()
            else e.currentTarget.blur()
          }
        }}
        placeholder="Search by subject or sender…"
        aria-label="Search emails"
        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-20 text-sm text-gray-900 transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 [&::-webkit-search-cancel-button]:hidden"
      />
      <div className="absolute inset-y-0 right-2 flex items-center gap-1">
        {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden="true" />}
        {pending && (
          <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium leading-5 text-gray-500 animate-panel-in dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 sm:inline-block">
            Enter
          </kbd>
        )}
        {value || applied ? (
          <button
            type="button"
            onClick={() => { onClear(); inputRef.current?.focus() }}
            aria-label="Clear search"
            className="rounded-md p-1 text-gray-400 transition-colors animate-panel-in hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className={`hidden rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium leading-5 text-gray-400 transition-opacity duration-200 dark:border-gray-700 dark:bg-gray-900 sm:inline-block ${focused ? 'opacity-0' : 'opacity-100'}`}>
            /
          </kbd>
        )}
      </div>
    </form>
  )
}

/** All time / Today / 7 days / 30 days, with a pill that slides to the choice. */
function TimeRangeControl({ value, onChange }) {
  // Padding changes at the sm breakpoint, so the pill re-measures on resize.
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 0))
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const { tabRefs, indicatorStyle } = useSegmentedTabIndicator(value || 'all', [viewportWidth])

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const idx = TIME_RANGES.findIndex(r => r.value === value)
    const next = TIME_RANGES[(idx + (e.key === 'ArrowRight' ? 1 : -1) + TIME_RANGES.length) % TIME_RANGES.length]
    onChange(next.value)
    tabRefs.current[next.value || 'all']?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label="Received"
      onKeyDown={onKeyDown}
      className="relative inline-flex h-9 flex-shrink-0 items-center rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800"
    >
      <span
        aria-hidden="true"
        className="absolute bottom-0.5 top-0.5 rounded-md bg-white shadow-sm ring-1 ring-black/5 transition-[left,width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] dark:bg-gray-700 dark:ring-white/5"
        style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
      />
      {TIME_RANGES.map(range => {
        const selected = range.value === value
        return (
          <button
            key={range.value || 'all'}
            ref={(el) => { tabRefs.current[range.value || 'all'] = el }}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={range.label}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(range.value)}
            className={`relative z-[1] h-8 whitespace-nowrap rounded-md px-2.5 text-[13px] font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 sm:px-3 ${
              selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {range.short}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Shows the quiet refresh is on while emails are in flight, and lets it be
 * paused. Hidden when there's nothing in flight to watch.
 */
function LiveIndicator({ inFlight, active, paused, lastUpdated, onToggle }) {
  if (!inFlight) return null
  const updated = lastUpdated ? `Last updated ${lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.` : ''
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={!paused}
      title={paused
        ? `Live updates paused. Select to resume. ${updated}`
        : `Updating every ${LIVE_INTERVAL_MS / 1000} seconds while ${inFlight} email${inFlight !== 1 ? 's are' : ' is'} queued or processing. Select to pause. ${updated}`}
      className={`inline-flex h-9 items-center gap-2 rounded-lg px-2.5 text-[13px] font-medium transition-colors duration-200 animate-panel-in focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
        active
          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/15'
          : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
      }`}
    >
      {active ? (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
      ) : (
        <Pause className="h-3 w-3" />
      )}
      <span>{active ? 'Live' : 'Paused'}</span>
    </button>
  )
}

function FilterPill({ label, onRemove, removeLabel, dot }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-navy-700 ring-1 ring-inset ring-navy-200/70 animate-panel-in dark:bg-navy-500/10 dark:text-navy-200 dark:ring-navy-400/20">
      {dot && <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
      <span className="max-w-[16rem] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="rounded-full p-0.5 transition-colors hover:bg-navy-100 dark:hover:bg-navy-400/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

// ── List ─────────────────────────────────────────────────────────────────────

// Email | Attachments | Status | Received | action — shared by header and rows.
const ROW_GRID = 'lg:grid lg:grid-cols-[minmax(0,1fr)_7rem_8.5rem_10rem_4.75rem] lg:items-center lg:gap-4'

function ColumnHeader() {
  return (
    <div className={`hidden px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 sm:px-5 ${ROW_GRID}`}>
      <span className="pl-12">Email</span>
      <span>Attachments</span>
      <span>Status</span>
      <span>Received</span>
      <span className="sr-only">Actions</span>
    </div>
  )
}

/** Sticks under the app's 4rem top bar while its group is on screen. */
function DateHeader({ label, count }) {
  return (
    <div className="sticky top-16 z-10 flex items-center gap-2 border-y border-gray-100 bg-gray-50/90 px-4 py-2 backdrop-blur-sm supports-[backdrop-filter]:bg-gray-50/75 dark:border-gray-800 dark:bg-gray-900/90 dark:supports-[backdrop-filter]:bg-gray-900/75 sm:px-5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</h2>
      <span className="text-[11px] font-medium tabular-nums text-gray-400 dark:text-gray-500">{count}</span>
    </div>
  )
}

function EmailRow({ email, index, highlight }) {
  const meta = getStatusMeta(email.status)
  const sender = parseSender(email.sender)
  const received = email.received_at || email.created_at
  const docs = email.document_count || 0
  const href = `/emails/${email.id}`
  const accent = email.status === 'processing' || email.status === 'failed'

  return (
    <li
      className="group relative animate-rise-in"
      style={{ '--rise-delay': `${Math.min(index, 14) * 28}ms` }}
    >
      {/* Left accent: always on for work in flight or failed, on hover otherwise. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute bottom-2 left-0 top-2 w-[3px] origin-center rounded-r-full transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${meta.bar} ${
          accent ? `scale-y-100 ${email.status === 'processing' ? 'motion-safe:animate-pulse' : ''}` : 'scale-y-0 group-hover:scale-y-100'
        }`}
      />
      <div
        className={`relative flex items-center gap-3 px-4 py-3 transition-colors duration-200 ${highlight === 'new' ? 'email-row-flash' : ''} group-hover:bg-gray-50/80 has-[a:focus-visible]:bg-navy-50/60 dark:group-hover:bg-gray-800/40 dark:has-[a:focus-visible]:bg-navy-500/10 sm:px-5 ${ROW_GRID}`}
      >
        {/* Sender + subject. The subject link stretches over the whole row. */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span
            aria-hidden="true"
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-110 ${avatarTone(sender.address || sender.name)}`}
          >
            {initialsFor(sender)}
          </span>
          <div className="min-w-0 flex-1">
            <Link
              to={href}
              className="block truncate text-sm font-medium text-gray-900 transition-colors after:absolute after:inset-0 after:content-[''] focus:outline-none group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-navy-300"
            >
              {email.subject || 'No subject'}
            </Link>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="truncate" title={email.sender || undefined}>
                {sender.name}
                {sender.address && sender.address !== sender.name && (
                  <span className="text-gray-400 dark:text-gray-500"> &lt;{sender.address}&gt;</span>
                )}
              </span>
              {email.is_shared_mailbox && email.mailbox_email && (
                <span
                  className="inline-flex max-w-full items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-px text-[11px] font-medium text-indigo-600 ring-1 ring-inset ring-indigo-500/15 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/20"
                  title={`Shared mailbox: ${email.mailbox_email}`}
                >
                  <Inbox className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{email.mailbox_email}</span>
                </span>
              )}
            </div>
            {/* Below lg the attachment count and time get a line of their own. */}
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 lg:hidden">
              <Paperclip className="h-3 w-3" aria-label="Attachments" />
              <span className="tabular-nums">{docs}</span>
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">&middot;</span>
              <time dateTime={received || undefined} title={formatDateTime(received)}>{formatTimeAgo(received)}</time>
            </div>
            {email.request_id && (
              <Link
                to={`/requests/${email.request_id}`}
                className="relative z-[1] mt-1 inline-flex items-center gap-0.5 text-xs font-medium text-gray-400 transition-colors hover:text-navy-600 dark:text-gray-500 dark:hover:text-navy-400"
              >
                View request
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>

        <div className="hidden lg:block">
          <span
            className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium tabular-nums ${
              docs > 0
                ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                : 'text-gray-400 dark:text-gray-500'
            }`}
            title={`${docs} attachment${docs !== 1 ? 's' : ''}`}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {docs}
          </span>
        </div>

        <div className="flex-shrink-0">
          <EmailStatusBadge status={email.status} changed={highlight === 'status'} />
        </div>

        <div className="hidden min-w-0 lg:block">
          <time dateTime={received || undefined} className="block truncate text-sm text-gray-700 dark:text-gray-300" title={formatDateTime(received)}>
            {formatDate(received)}
          </time>
          <span className="block truncate text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(received)}</span>
        </div>

        {/* Part of the row's link, so it's decoration for assistive tech. */}
        <div aria-hidden="true" className="hidden justify-end lg:flex">
          <span className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:border-navy-200 group-hover:bg-white group-hover:text-navy-700 group-hover:shadow-sm dark:border-gray-700 dark:text-gray-300 dark:group-hover:border-navy-500/40 dark:group-hover:bg-gray-800 dark:group-hover:text-navy-200">
            <Eye className="h-3.5 w-3.5" />
            View
          </span>
        </div>
      </div>
    </li>
  )
}

const EMPTY_STATUS_MESSAGES = {
  pending: 'Nothing is waiting in the queue.',
  processing: 'Nothing is being processed at the moment.',
  processed: 'No emails have been processed yet.',
  failed: 'No emails have failed. Everything is running smoothly.',
}

function EmptyState({ status, search, timeLabel, isReader, onClear, onIngest }) {
  const statusLabel = status ? getStatusMeta(status).label : null
  const filtered = Boolean(statusLabel || search || timeLabel)

  const timePhrase = timeLabel === 'Today' ? 'today' : timeLabel ? `in the ${timeLabel.toLowerCase()}` : ''

  let message
  if (search) message = <>Nothing{statusLabel ? <> in {statusLabel}</> : null} matches &ldquo;{search}&rdquo;. Try part of a subject line or a sender&rsquo;s address.</>
  else if (statusLabel && timePhrase) message = <>No {statusLabel.toLowerCase()} emails received {timePhrase}.</>
  else if (statusLabel) message = EMPTY_STATUS_MESSAGES[status] || <>No emails are {statusLabel.toLowerCase()}.</>
  else if (timePhrase) message = <>No emails were received {timePhrase}.</>
  else message = 'Emails you ingest, and those arriving from connected mailboxes, will appear here with their attachments.'

  return (
    <div className="px-6 py-16 text-center animate-panel-in">
      <div className="relative mx-auto h-20 w-20">
        <div className="absolute inset-0 rotate-6 rounded-2xl bg-navy-100/60 transition-transform duration-500 dark:bg-navy-500/10" />
        <div className="absolute inset-0 -rotate-3 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700" />
        <div className="absolute inset-0 flex items-center justify-center">
          {filtered
            ? <SearchX className="h-8 w-8 text-navy-500 dark:text-navy-300" />
            : <MailPlus className="h-8 w-8 text-navy-500 dark:text-navy-300" />}
        </div>
      </div>
      <h3 className="mt-5 text-base font-semibold text-gray-900 dark:text-gray-100">
        {filtered ? 'No matching emails' : 'No emails yet'}
      </h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{message}</p>
      {filtered ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-sm active:translate-y-0 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </button>
      ) : !isReader && (
        <button
          type="button"
          onClick={onIngest}
          className="btn btn-primary group mt-5 shadow-sm"
        >
          <Plus className="mr-2 h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
          Ingest an email
        </button>
      )}
    </div>
  )
}

// ── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, perPage, onPage, onPerPage }) {
  return (
    <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3.5 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <label className="flex items-center gap-2">
        Rows per page
        <select
          value={perPage}
          onChange={(e) => onPerPage(Number(e.target.value))}
          className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </label>
      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>
          {generatePageNumbers(page, totalPages).map((pg, i) => (
            <React.Fragment key={`${pg}-${i}`}>
              {pg === '...' ? (
                <span className="px-1.5 text-gray-400 dark:text-gray-500">…</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPage(pg)}
                  aria-current={pg === page ? 'page' : undefined}
                  className={`h-8 min-w-[2rem] rounded-lg px-2 text-sm font-medium tabular-nums transition-all duration-200 ${
                    pg === page
                      ? 'bg-navy-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 active:scale-95 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  {pg}
                </button>
              )}
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  )
}

function generatePageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }
  if (current <= 3) {
    return [1, 2, 3, '...', total]
  }
  if (current >= total - 2) {
    return [1, '...', total - 2, total - 1, total]
  }
  return [1, '...', current - 1, current, current + 1, '...', total]
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SKELETON_WIDTHS = ['w-72', 'w-56', 'w-80', 'w-64', 'w-48', 'w-60']

function ListSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="border-y border-gray-100 bg-gray-50/90 px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900/90 sm:px-5">
        <div className="skeleton h-3 w-16" />
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800/80">
        {SKELETON_WIDTHS.map((width, i) => (
          <li key={i} className={`flex items-center gap-3 px-4 py-3 sm:px-5 ${ROW_GRID}`}>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="skeleton h-9 w-9 flex-shrink-0 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className={`skeleton h-4 max-w-full ${width}`} />
                <div className="skeleton h-3 w-40" />
              </div>
            </div>
            <div className="skeleton hidden h-5 w-10 lg:block" />
            <div className="skeleton h-5 w-20 rounded-full" />
            <div className="hidden space-y-1.5 lg:block">
              <div className="skeleton h-3.5 w-24" />
              <div className="skeleton h-3 w-16" />
            </div>
            <div className="skeleton hidden h-6 w-14 justify-self-end lg:block" />
          </li>
        ))}
      </ul>
    </div>
  )
}

export default Emails
